// ChainCard — renderer for viz: chain. An expiry-explorer when the payload
// carries `expiries[]`: a horizontal ExpiryStrip (future-only, chronological)
// above the selected expiry's full flow-chain (Deribit-style Calls | Strike |
// Puts, each side net-bar · Notional · Premium · Trades), opening on
// `default_expiry`. Each side also carries an outermost OI column (per-instrument
// open interest, contract count). Net bars scale to max|net| WITHIN the selected
// expiry; the strike nearest spot is amber. Falls back to the flat default-expiry ladder
// (`report.rows`) when `expiries[]` is absent. Strikes are sorted ASCENDING
// (low -> high; copy before sort — payload not mutated). Selection state local.

import { useContext, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import {
  fmtAsOf,
  fmtCount,
  fmtNotional,
  fmtUsd,
  FlowMetric,
  futureExpiries,
  maxAbs,
  METRIC_ORDER,
  resolveNotionalUnit,
} from '../../../../providers/options-flow/options-flow.helpers';
import { OptionsFlowContext } from '../../../../providers/options-flow/options-flow-provider';
import { ChainReport, ChainRow } from '../../../../providers/options-flow/types';
import { FlowCard } from '../flow-card';
import { FlowEmptyState } from '../flow-empty-state';
import { DivergingBar } from '../diverging-bar';
import { ExpiryStrip } from '../expiry-strip';
import { flowTableSx, tdSx, themedScrollbarSx, thNumSx, thSx } from '../table-chrome';

interface ChainCardProps {
  report: ChainReport;
}

// The strike closest to spot, or null when spot is absent (no amber marker).
const nearestStrikeToSpot = (rows: ChainRow[], spot: number | null | undefined): number | null => {
  if (spot == null || rows.length === 0) return null;
  return rows.reduce((best, r) =>
    Math.abs(r.strike - spot) < Math.abs(best.strike - spot) ? r : best,
  ).strike;
};

const MONO = 'ui-monospace, Menlo, Consolas, monospace';
const numOrDash = (v: number | null | undefined, fmt: (n: number) => string): string =>
  v == null || Number.isNaN(v) ? '-' : fmt(v);

// Per-side density fields keyed by the canonical metric id. The chain uses a
// short 'Prem' label locally; only the ORDER comes from METRIC_ORDER.
const CHAIN_FIELDS: Record<
  FlowMetric,
  { call: keyof ChainRow; put: keyof ChainRow; label: string }
> = {
  notional: { call: 'call_notional', put: 'put_notional', label: 'Notional' },
  premium: { call: 'call_premium', put: 'put_premium', label: 'Prem' },
  trades: { call: 'call_trades', put: 'put_trades', label: 'Trades' },
};
// Reading OUTWARD from the strike, density is METRIC_ORDER (Notional · Premium ·
// Trades). The calls side is laid out left-to-right toward the strike, so its
// columns are the reverse; the puts side reads outward directly.
const CALL_ORDER: FlowMetric[] = [...METRIC_ORDER].reverse();
const PUT_ORDER: FlowMetric[] = [...METRIC_ORDER];

const numSx = { ...tdSx, textAlign: 'right' as const, fontFamily: MONO, fontSize: 11.5 };
const numHeadSx = { ...thNumSx, color: tokens.text.muted };
const groupDivider = { borderLeft: `1px solid ${tokens.border.default}` };

// The net-bar SVG width — shared by the bars and the header so the "Net" label
// lines up with the bar's zero-axis divider.
const NET_BAR_W = 84;

// "Net" header cell that anchors the label to the bar's zero-axis divider line
// (NOT the column edge). The bar is a fixed NET_BAR_W-wide SVG flush to the cell's
// INNER edge — right-aligned on the calls side, left-aligned on the puts side —
// with its divider at the SVG centre. We replicate that exact box (an inline
// NET_BAR_W spacer, same side-alignment) and centre "Net" within it, so the label
// sits over the divider regardless of how wide the column stretches.
const NetHeaderCell = ({ side }: { side: 'call' | 'put' }) => (
  <Box
    component="th"
    scope="col"
    sx={{
      ...numHeadSx,
      textAlign: side === 'call' ? 'right' : 'left',
      ...(side === 'put' ? groupDivider : {}),
    }}
  >
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        width: `${NET_BAR_W}px`,
        height: '1em',
        verticalAlign: 'middle',
      }}
    >
      <Box
        component="span"
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          whiteSpace: 'nowrap',
        }}
      >
        Net
      </Box>
    </Box>
  </Box>
);

// One expiry's full flow-chain, Deribit-style: Calls | Strike | Puts, ascending
// by strike (low -> high). Each side reads (outward from the strike) net-bar ·
// Notional · Premium · Trades · OI — mono, right-aligned. Bars scaled to max|net|
// WITHIN this ladder; nearest-spot strike amber, long-hand comma format.
// Notional/per-side-trade/OI fields are optional -> `-` until emitted.
const StrikeLadder = ({
  rows,
  spot,
  unit,
}: {
  rows: ChainRow[];
  spot: number | null | undefined;
  unit: string;
}) => {
  if (rows.length === 0) return <FlowEmptyState />;
  // Copy before sort — never mutate the payload.
  const sorted = [...rows].sort((a, b) => a.strike - b.strike);
  const atmStrike = nearestStrikeToSpot(sorted, spot);
  const barScale = maxAbs(sorted.flatMap((r) => [r.call_net, r.put_net]));
  // Metric-id -> formatter (notional binds the unit here); density cells look
  // them up by id so the columns follow METRIC_ORDER.
  const fmtFor: Record<FlowMetric, (v: number) => string> = {
    notional: (v) => fmtNotional(v, unit),
    premium: fmtUsd,
    trades: fmtCount,
  };

  return (
    <Box sx={{ overflowX: 'auto', ...themedScrollbarSx }}>
      <Box component="table" sx={flowTableSx}>
        <Box component="thead">
          {/* Level 1 — CALLS | STRIKE | PUTS */}
          <Box component="tr">
            <Box component="th" scope="colgroup" colSpan={5} sx={{ ...thSx, textAlign: 'center' }}>
              Calls
            </Box>
            <Box
              component="th"
              scope="col"
              rowSpan={2}
              sx={{ ...thSx, textAlign: 'center', ...groupDivider }}
            >
              Strike
            </Box>
            <Box
              component="th"
              scope="colgroup"
              colSpan={5}
              sx={{ ...thSx, textAlign: 'center', ...groupDivider }}
            >
              Puts
            </Box>
          </Box>
          {/* Level 2 — sub-labels, reading outward from the strike each side.
              Density order from the strike is METRIC_ORDER (Notional · Premium ·
              Trades); the calls side is mirrored, so its labels are reversed. */}
          <Box component="tr">
            <Box component="th" scope="col" sx={numHeadSx}>
              OI
            </Box>
            {CALL_ORDER.map((m) => (
              <Box key={`call-h-${m}`} component="th" scope="col" sx={numHeadSx}>
                {CHAIN_FIELDS[m].label}
              </Box>
            ))}
            <NetHeaderCell side="call" />
            <NetHeaderCell side="put" />
            {PUT_ORDER.map((m) => (
              <Box key={`put-h-${m}`} component="th" scope="col" sx={numHeadSx}>
                {CHAIN_FIELDS[m].label}
              </Box>
            ))}
            <Box component="th" scope="col" sx={numHeadSx}>
              OI
            </Box>
          </Box>
        </Box>
        <Box component="tbody">
          {sorted.map((r) => {
            const isAtm = atmStrike != null && r.strike === atmStrike;
            return (
              <Box component="tr" key={r.strike}>
                {/* CALL side — mirrored, so left-to-right is the reverse of the
                    outward density order (OI outermost, …, Net(bar) nearest the
                    strike). */}
                <Box component="td" sx={numSx}>
                  {numOrDash(r.call_oi, fmtCount)}
                </Box>
                {CALL_ORDER.map((m) => (
                  <Box key={`call-${m}`} component="td" sx={numSx}>
                    {numOrDash(r[CHAIN_FIELDS[m].call] as number | null | undefined, fmtFor[m])}
                  </Box>
                ))}
                <Box component="td" sx={{ ...tdSx, textAlign: 'right' }}>
                  <DivergingBar
                    value={r.call_net}
                    max={barScale}
                    width={NET_BAR_W}
                    height={13}
                    showValue
                  />
                </Box>

                {/* STRIKE */}
                <Box
                  component="td"
                  data-atm={isAtm ? 'true' : undefined}
                  sx={{
                    ...tdSx,
                    textAlign: 'center',
                    fontFamily: MONO,
                    fontWeight: 700,
                    ...groupDivider,
                  }}
                  // Inline (not sx) so the amber ATM marker is assertable and
                  // beats tdSx's default colour by inline specificity.
                  style={{ color: isAtm ? tokens.flow.accent : tokens.text.secondary }}
                >
                  {r.strike.toLocaleString('en-US')}
                </Box>

                {/* PUT side — Net(bar) nearest the strike, then density reading
                    outward in METRIC_ORDER. */}
                <Box component="td" sx={{ ...tdSx, textAlign: 'left', ...groupDivider }}>
                  <DivergingBar
                    value={r.put_net}
                    max={barScale}
                    width={NET_BAR_W}
                    height={13}
                    showValue
                  />
                </Box>
                {PUT_ORDER.map((m) => (
                  <Box key={`put-${m}`} component="td" sx={numSx}>
                    {numOrDash(r[CHAIN_FIELDS[m].put] as number | null | undefined, fmtFor[m])}
                  </Box>
                ))}
                <Box component="td" sx={numSx}>
                  {numOrDash(r.put_oi, fmtCount)}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export const ChainCard = ({ report }: ChainCardProps) => {
  const spot = report.spot ?? report.meta.spot;
  const unit = resolveNotionalUnit(report.meta);

  // Live expiries only (settlement-aware; drops an expiry at the active venue's
  // settlement hour UTC). Shared filter with the ranked expiry buttons; never
  // blanks.
  const { activeVenue } = useContext(OptionsFlowContext);
  const settlementHour = activeVenue?.settlement_hour_utc;
  const stripExpiries = useMemo(
    () => futureExpiries(report.expiries ?? [], undefined, settlementHour),
    [report.expiries, settlementHour],
  );

  const hasExpiries = stripExpiries.length > 0;
  const defaultExpiry =
    report.default_expiry && stripExpiries.some((e) => e.expiry === report.default_expiry)
      ? report.default_expiry
      : (stripExpiries[0]?.expiry ?? null);

  const [selectedExpiry, setSelectedExpiry] = useState<string | null>(null);
  const activeExpiry = selectedExpiry ?? defaultExpiry;

  const activeLadder = useMemo(
    () => stripExpiries.find((e) => e.expiry === activeExpiry)?.rows ?? [],
    [stripExpiries, activeExpiry],
  );

  const caption =
    spot != null
      ? `Net taker premium by strike · spot ${fmtUsd(spot)}`
      : 'Net taker premium by strike';

  // Prefer the full ISO `as_of` (date + time UTC) over the date-only
  // `as_of_date`, so the badge reflects data-freshness to the minute.
  const asOf = fmtAsOf(report.meta.as_of ?? report.meta.as_of_date);

  return (
    <FlowCard title={report.title} caption={caption} badge={asOf ? `AS-OF ${asOf}` : undefined}>
      {!hasExpiries ? (
        // Legacy fallback — the flat default-expiry ladder.
        <StrikeLadder rows={report.rows ?? []} spot={spot} unit={unit} />
      ) : (
        <Box>
          <ExpiryStrip
            expiries={stripExpiries}
            activeExpiry={activeExpiry}
            onSelect={setSelectedExpiry}
          />
          <StrikeLadder rows={activeLadder} spot={spot} unit={unit} />
        </Box>
      )}
    </FlowCard>
  );
};
